import { storage } from './storage';
import { api } from './api';

export interface SyncQueueItem {
  docId: string;
  content: string;
  title: string;
  action: 'update' | 'create';
  timestamp: number;
}

type StatusCallback = (isOnline: boolean) => void;

class OfflineService {
  private _isOnline: boolean = navigator.onLine;
  private syncQueue: SyncQueueItem[] = [];
  private statusCallbacks: StatusCallback[] = [];
  private isSyncing: boolean = false;

  init(): void {
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
    this.loadQueueFromStorage();

    // Si estamos online al iniciar, procesar cola pendiente
    if (this._isOnline && this.syncQueue.length > 0) {
      this.processSyncQueue();
    }
  }

  get isOnline(): boolean {
    return this._isOnline;
  }

  private handleOffline(): void {
    this._isOnline = false;
    this.notifyStatusChange();
  }

  private handleOnline(): void {
    this._isOnline = true;
    this.notifyStatusChange();
    this.processSyncQueue();
  }

  private notifyStatusChange(): void {
    for (const callback of this.statusCallbacks) {
      callback(this._isOnline);
    }
  }

  onStatusChange(callback: StatusCallback): void {
    this.statusCallbacks.push(callback);
  }

  queueChange(docId: string, content: string, title: string, action: 'update' | 'create' = 'update'): void {
    // Buscar si ya hay un cambio pendiente para este documento
    const existingIndex = this.syncQueue.findIndex(item => item.docId === docId);

    if (existingIndex >= 0) {
      // Actualizar el existente
      this.syncQueue[existingIndex] = {
        docId,
        content,
        title,
        action,
        timestamp: Date.now()
      };
    } else {
      // Añadir nuevo
      this.syncQueue.push({
        docId,
        content,
        title,
        action,
        timestamp: Date.now()
      });
    }

    this.saveQueueToStorage();
  }

  async processSyncQueue(): Promise<void> {
    if (!this._isOnline || this.isSyncing || this.syncQueue.length === 0) {
      return;
    }

    this.isSyncing = true;

    const itemsToSync = [...this.syncQueue];
    const syncedIds: string[] = [];

    for (const item of itemsToSync) {
      try {
        if (item.action === 'update') {
          const response = await api.updateDocument(item.docId, {
            content: item.content,
            title: item.title
          });

          if (response.success) {
            syncedIds.push(item.docId);
            storage.removeDraft(item.docId);
          }
        }
        // TODO: handle 'create' action
      } catch (e) {
        console.error(`Error syncing document ${item.docId}:`, e);
      }
    }

    // Eliminar los sincronizados de la cola
    this.syncQueue = this.syncQueue.filter(item => !syncedIds.includes(item.docId));
    this.saveQueueToStorage();

    this.isSyncing = false;
  }

  private loadQueueFromStorage(): void {
    const stored = localStorage.getItem('notepad_sync_queue');
    if (stored) {
      try {
        this.syncQueue = JSON.parse(stored);
      } catch (e) {
        this.syncQueue = [];
      }
    }
  }

  private saveQueueToStorage(): void {
    localStorage.setItem('notepad_sync_queue', JSON.stringify(this.syncQueue));
  }

  getPendingCount(): number {
    return this.syncQueue.length;
  }

  hasPendingChanges(): boolean {
    return this.syncQueue.length > 0;
  }
}

export const offlineService = new OfflineService();
